import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import DayPicker from 'react-day-picker';
import dateFnsFormat from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import formatISO from 'date-fns/formatISO';
import styled from '@emotion/styled';
import orderBy from 'lodash/orderBy';
import { CalendarBlank } from '@phosphor-icons/react';
import { Palette } from '../../constants';
import { DateInput } from '../date-input';
import { DatePickerNav } from './DatePickerNav';
import { Box } from '../box';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '../modal';
const DatePickerInputWrapper = styled(Box) `
  .DayPicker {
    min-height: 400px;
  }
  .DayPicker-wrapper {
    outline: none;
  }

  .DayPicker .DayPicker-Weekday,
  .DayPicker .DayPicker-Day {
    font-family: ${({ theme }) => theme.fonts.body};
  }

  //  ===== START DAY STYLING ====
  // :hover
  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--selected
    ):not(.DayPicker-Day--outside):hover {
    // Note: I don't like how it's called success here... Do something
    // Takes "success" color and reduces color to 30%
    background-color: ${({ theme }) => theme.colors.success}${Math.floor(0.3 * 255).toString(16)};
  }
  // selected
  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ) {
    background-color: ${({ theme }) => theme.colors.success};
    color: ${({ theme }) => theme.colors.primary};
  }
  // selected + :hover
  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ):hover {
  }
  // disabled
  .DayPicker-Day.DayPicker-Day--disabled:not(.DayPicker-Day--outside) {
    color: ${Palette.gray50} !important;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='black' /></svg>");
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%, auto;
  }
  .DayPicker-Day.DayPicker-Day--past.DayPicker-Day--disabled {
    color: ${Palette.gray50} !important;
    text-decoration: none;
    background: none;
  }
  // available
  .DayPicker-Day {
    color: ${({ theme }) => theme.colors.primary} !important;
    font-size: ${({ theme }) => theme.fontSizes.s}px;
    width: 40px;
    height: 40px;

    justify-content: center;
    align-items: center;
    border-radius: 100%;
    text-align: center;
    display: inline-flex;

    // large viewport+
    ${({ theme }) => theme.mediaQueries.l} {
      font-size: ${({ theme }) => theme.fontSizes.l}px;
      width: 60px;
      height: 60px;
    }
  }
  // today's date
  .DayPicker-Day--today {
    position: relative;
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};

    // Dot under date
    &::before {
      content: '';
      position: absolute;
      bottom: ${({ theme }) => theme.space.xs}px;
      left: 50%;
      transform: translateX(-50%);
      border-radius: 50%;
      width: ${({ theme }) => theme.space.xxs}px;
      height: ${({ theme }) => theme.space.xxs}px;
      background-color: ${({ theme }) => theme.colors.primary} !important;
    }

    &.DayPicker-Day--outside {
      &::before {
        display: none;
      }
    }
  }
  // Today & disabled
  .DayPicker-Day--disabled.DayPicker-Day--today {
    &::before {
      background-color: ${Palette.gray50} !important;
    }
  }
  //  ===== END DAY STYLING ====

  //  ===== START WEEKDAY STYLING ====
  .DayPicker-WeekdaysRow {
    display: flex;
    justify-content: space-between;
    padding-right: ${({ theme }) => theme.space.xs}px;
    padding-left: ${({ theme }) => theme.space.xs}px;
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};

    ${({ theme }) => theme.mediaQueries.l} {
      padding-right: ${({ theme }) => theme.space.s}px;
      padding-left: ${({ theme }) => theme.space.s}px;
    }

    & > .DayPicker-Weekday {
      font-size: ${({ theme }) => theme.fontSizes.m}px;
      color: ${({ theme }) => theme.colors.primary} !important;
    }
  }
  //  ===== END WEEKDAY STYLING ====

  //  ===== START WEEK STYLING ====
  .DayPicker-Week {
    display: flex;
    justify-content: space-between;
  }
  //  ===== END WEEK STYLING ====

  //  ===== START MONTH STYLING ====
  .DayPicker-Months > .DayPicker-Month {
    flex: 1;
    ${({ theme }) => theme.mediaQueries.l} {
      min-height: 395px;
    }
  }
  //  ===== END MONTH STYLING ====
`;
//  ===== START DATE PARSER LOGIC ====
const DATE_FORMAT = 'MM/dd/yyyy';
function formatDate(date) {
    return dateFnsFormat(date, DATE_FORMAT);
}
export function filterDateValue(value) {
    try {
        if (typeof value === 'undefined' || value === '')
            return undefined;
        // Convert to UTC String then to Date again to fix off-by-1 bug
        const dateInUTC = new Date(value).toLocaleString('en-US', {
            timeZone: 'UTC',
        });
        return new Date(dateInUTC);
    }
    catch (_) {
        return undefined;
    }
}
//  ===== END DATE PARSER LOGIC ====
const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export const DatePickerInput = ({ onChange, minDate = undefined, maxDate = undefined, value = '', disabledDates = [], label = 'Date', name = 'date', representation = 'complete', ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(filterDateValue(value));
    const range = {};
    const formattedMinDate = minDate ? startOfDay(new Date(minDate)) : undefined;
    const formattedMaxDate = maxDate ? startOfDay(new Date(maxDate)) : undefined;
    // Only assign range if min and max dates are provided
    if (formattedMinDate !== undefined) {
        range.before = formattedMinDate;
    }
    if (formattedMaxDate !== undefined) {
        range.after = formattedMaxDate;
    }
    const blockedDays = [
        range,
        ...disabledDates
            .map((date) => {
            const parsedDate = new Date(date);
            if (isNaN(parsedDate.getTime())) {
                console.warn(`Invalid date string found: ${date}`);
                return null;
            }
            return parsedDate;
        })
            .filter((d) => d !== null),
    ];
    // Dates that are before current date
    const customModifiers = {
        past: { before: new Date() },
    };
    // TODO: figure out how to pass in change event
    const handleOnChange = (day) => {
        // NOTE: We're mocking this until we can fire onChange synthetically
        // event.target.value
        const mockEvent = {
            target: {
                value: representation === 'date'
                    ? formatISO(startOfDay(day), { representation })
                    : startOfDay(day).toISOString(),
                name,
            },
        };
        onChange === null || onChange === void 0 ? void 0 : onChange(mockEvent);
    };
    // Return empty string if date is undefined
    const inputValue = useMemo(() => {
        try {
            return typeof selectedDate !== 'undefined'
                ? formatDate(startOfDay(selectedDate))
                : '';
        }
        catch (_) {
            return '';
        }
    }, [selectedDate]);
    const handleInputKeyPress = (e) => {
        // Todo: Update DateInput to handle input editing
        // Open Modal only on spacebar and click events
        if ('key' in e) {
            setIsOpen(true);
        }
    };
    /**
     * Defines edges for start & end range. NOTE: This is separate from the disabled date logic.
     *
     * Ignores selectedDate changes.
     */
    const absoluteDateRanges = useMemo(() => {
        const potentialStartDates = [selectedDate, formattedMinDate].filter((date) => !!date);
        const potentialEndDates = [selectedDate, formattedMaxDate].filter((date) => !!date);
        return {
            start: orderBy(potentialStartDates, [(date) => date === null || date === void 0 ? void 0 : date.getTime()], ['asc'])[0],
            end: orderBy(potentialEndDates, [(date) => date === null || date === void 0 ? void 0 : date.getTime()], ['desc'])[0],
        };
    }, [formattedMinDate, formattedMaxDate]);
    const getInitialMonth = () => {
        const currentDate = new Date();
        if (selectedDate)
            return selectedDate;
        const isCurrentDateAfterMinDate = formattedMinDate
            ? currentDate.getTime() > (formattedMinDate === null || formattedMinDate === void 0 ? void 0 : formattedMinDate.getTime())
            : true;
        const isCurrentDateBeforeMaxDate = formattedMaxDate
            ? currentDate.getTime() < (formattedMaxDate === null || formattedMaxDate === void 0 ? void 0 : formattedMaxDate.getTime())
            : true;
        if (isCurrentDateAfterMinDate && isCurrentDateBeforeMaxDate) {
            return currentDate;
        }
        if (formattedMinDate !== undefined)
            return range.before;
        return undefined;
    };
    return (_jsxs(DatePickerInputWrapper, { children: [_jsx(DateInput, { readOnly: true, rightSlot: _jsx(CalendarBlank, { role: "img", "aria-label": "calendar-icon", onClick: () => setIsOpen(true) }), label: label, 
                // Disable typing for user
                value: inputValue, 
                // Open modal if input is "touched"
                onMouseDown: (event) => {
                    event.preventDefault();
                    setIsOpen(true);
                }, onKeyPress: (event) => {
                    event.preventDefault();
                    handleInputKeyPress(event);
                }, name: name, ...props }), _jsxs(Modal, { isOpen: isOpen, setIsOpen: setIsOpen, children: [_jsx(ModalOverlay, {}), _jsx(ModalContent, { children: _jsx(ModalBody, { flexDirection: "column", marginBottom: "none", children: _jsx(DayPicker, { modifiers: customModifiers, 
                                // Convert to date object if selectedDate is string
                                selectedDays: selectedDate, onDayClick: (day, modifiers) => {
                                    if (!(modifiers === null || modifiers === void 0 ? void 0 : modifiers.disabled)) {
                                        const dayAtStart = startOfDay(day);
                                        setSelectedDate(dayAtStart);
                                        handleOnChange(dayAtStart);
                                        setIsOpen(false);
                                    }
                                }, onDayKeyDown: (day, modifiers, e) => {
                                    if (!(modifiers === null || modifiers === void 0 ? void 0 : modifiers.disabled) &&
                                        (e.key === 'Space' || e.key === 'Enter')) {
                                        const dayAtStart = startOfDay(day);
                                        setSelectedDate(dayAtStart);
                                        handleOnChange(dayAtStart);
                                        if (e.key === 'Enter')
                                            setIsOpen(false);
                                    }
                                }, fromMonth: absoluteDateRanges.start, toMonth: absoluteDateRanges.end, weekdaysShort: WEEKDAYS_SHORT, disabledDays: blockedDays, 
                                // Prevent showing user disabled month
                                initialMonth: getInitialMonth(), navbarElement: ({ 
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-expect-error
                                month, 
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-expect-error
                                showPreviousButton, 
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-expect-error
                                showNextButton, 
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-expect-error
                                onPreviousClick, 
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-expect-error
                                onNextClick, }) => (_jsx(DatePickerNav, { month: month, canNavigateToPreviousMonth: showPreviousButton, canNavigateToNextMonth: showNextButton, onPreviousClick: onPreviousClick, onNextClick: onNextClick, onCloseClick: () => setIsOpen(false) })), captionElement: _jsx(_Fragment, {}) }) }) })] })] }));
};
//# sourceMappingURL=data:application/json;base64,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